// Block editor — split view (motiflink §2.4, M4 design note §8).
//
// LEFT  a declarative field editor generated from BLOCK_SCHEMAS. Arabic copy
//       writes `content[key]`; the English tab writes `translations.en[key]`.
// RIGHT the marketing site's own `/preview/block` route in an iframe, so what
//       the editor sees is the real renderer with the real CSS, not a second
//       implementation that drifts.
//
// An unknown block type (a row whose type this admin build predates) falls
// back to raw JSON textareas — the pre-M4 editor, kept as graceful
// degradation so a new block type shipped API-first is still editable here.

import { Alert, Button, Input, Segmented, Textarea } from '@fajrarabi/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useEffect, useMemo, useRef, useState } from 'react';

import { FieldEditor, type BlockDraft } from './-block-fields';

import { ApiError } from '~/lib/api';
import { PREVIEW_VIEWPORTS, blockPreviewUrl, type PreviewViewport } from '~/lib/block-preview';
import { blockLabel, blockSchemaFor } from '~/lib/block-schemas';
import { getPage, updateBlock, type BlockSummary } from '~/lib/pages';

const PREVIEW_DEBOUNCE_MS = 350;

// The trailing underscore on `$id_` opts this route OUT of nesting under the
// page editor — the block editor replaces that screen rather than rendering
// inside it, so `_authed.pages.$id.tsx` needs no <Outlet/>.
export const Route = createFileRoute('/_authed/pages/$id_/blocks/$blockId')({
  component: BlockEditorPage,
});

function BlockEditorPage() {
  const { id, blockId } = Route.useParams();

  const pageQuery = useQuery({
    queryKey: ['pages', 'detail', id],
    queryFn: ({ signal }) => getPage(id, signal),
    staleTime: 2_000,
  });

  if (pageQuery.isLoading) {
    return <p className="py-16 text-center text-sm text-admin-500">Loading…</p>;
  }

  const block = pageQuery.data?.blocks.find((b) => b.id === blockId);
  if (pageQuery.error || !pageQuery.data || !block) {
    return (
      <div className="mx-auto max-w-2xl py-10">
        <Alert tone="danger">
          {pageQuery.error ? 'Could not load this page.' : 'That block no longer exists.'}
        </Alert>
        <BackLink pageId={id} className="mt-4" />
      </div>
    );
  }

  return (
    <BlockEditor key={block.id} pageId={id} pageSlug={pageQuery.data.page.slug} block={block} />
  );
}

function BackLink({ pageId, className }: { pageId: string; className?: string }) {
  const navigate = useNavigate();
  return (
    <button
      type="button"
      onClick={() => void navigate({ to: '/pages/$id', params: { id: pageId } })}
      className={`text-sm font-medium text-admin-600 transition-colors hover:text-admin-900 ${className ?? ''}`}
    >
      ← Back to page
    </button>
  );
}

function BlockEditor({
  pageId,
  pageSlug,
  block,
}: {
  pageId: string;
  pageSlug: string;
  block: BlockSummary;
}) {
  const qc = useQueryClient();
  const schema = blockSchemaFor(block.type);

  const [title, setTitle] = useState(block.title ?? '');
  const [draft, setDraft] = useState<BlockDraft>(() => ({
    content: block.content ?? {},
    settings: block.settings ?? {},
    translations: block.translations ?? {},
  }));
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  const baseline = useMemo(
    () =>
      JSON.stringify({
        title: block.title ?? '',
        content: block.content ?? {},
        settings: block.settings ?? {},
        translations: block.translations ?? {},
      }),
    [block],
  );
  const dirty = JSON.stringify({ title, ...draft }) !== baseline;

  const save = useMutation({
    mutationFn: () =>
      updateBlock(pageId, block.id, {
        title: title.trim() === '' ? null : title.trim(),
        content: draft.content,
        settings: draft.settings,
        translations: pruneOverlays(draft.translations),
      }),
    onSuccess: () => {
      setError(null);
      setSaved(true);
      void qc.invalidateQueries({ queryKey: ['pages', 'detail', pageId] });
      window.setTimeout(() => setSaved(false), 2500);
    },
    onError: (err) => {
      setSaved(false);
      if (err instanceof ApiError) {
        setError(
          err.details ? `${err.message}\n${JSON.stringify(err.details, null, 2)}` : err.message,
        );
      } else {
        setError('Save failed.');
      }
    },
  });

  // Warn on navigating away with unsaved work — this form holds enough typing
  // that losing it to a stray back-button is a real cost.
  useEffect(() => {
    if (!dirty) return;
    const onBeforeUnload = (e: BeforeUnloadEvent) => e.preventDefault();
    window.addEventListener('beforeunload', onBeforeUnload);
    return () => window.removeEventListener('beforeunload', onBeforeUnload);
  }, [dirty]);

  return (
    <div className="mx-auto max-w-[1600px]">
      <header className="flex flex-wrap items-start justify-between gap-4">
        <div className="min-w-0">
          <BackLink pageId={pageId} />
          <h1 className="mt-2 truncate text-2xl font-semibold text-admin-900">
            {blockLabel(block.type)}
          </h1>
          <p className="mt-1 text-sm text-admin-500">
            <span className="font-mono text-xs">{block.type}</span> on{' '}
            <span className="font-mono text-xs">{pageSlug === '' ? '/' : `/${pageSlug}`}</span>
            {' · '}
            {schema?.description ?? 'Unknown block type — editing raw JSON.'}
          </p>
        </div>
        <div className="flex items-center gap-3">
          {saved && <span className="text-sm font-medium text-leaf">Saved</span>}
          {dirty && !saved && <span className="text-sm text-admin-500">Unsaved changes</span>}
          <Button onClick={() => save.mutate()} disabled={!dirty} loading={save.isPending}>
            Save block
          </Button>
        </div>
      </header>

      {error && (
        <pre
          role="alert"
          className="mt-4 max-h-56 overflow-auto whitespace-pre-wrap rounded-xl bg-heart/10 p-4 text-xs leading-relaxed text-heart"
        >
          {error}
        </pre>
      )}

      <div className="mt-6 grid gap-6 xl:grid-cols-[minmax(0,7fr)_minmax(0,9fr)]">
        {/* ── LEFT: fields ── */}
        <div className="space-y-5">
          <section className="rounded-2xl border border-admin-200 bg-white p-5">
            <label className="block">
              <span className="text-sm font-semibold text-admin-900">Admin label</span>
              <p className="mt-1 text-xs text-admin-500">
                Shown only in this console, to tell two blocks of the same type apart. Never
                rendered on the site, so it is not translated.
              </p>
              <Input
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder={blockLabel(block.type)}
                className="mt-2 text-sm"
              />
            </label>
          </section>

          {schema ? (
            <>
              <section className="space-y-5 rounded-2xl border border-admin-200 bg-white p-5">
                <h2 className="text-sm font-semibold uppercase tracking-wider text-admin-400">
                  Content
                </h2>
                <p className="-mt-3 text-xs leading-relaxed text-admin-500">
                  The <span className="font-semibold">العربية</span> tab is the base value; the{' '}
                  <span className="font-semibold">English</span> tab is an override. Leave English
                  blank and English visitors see the Arabic — deliberate, because a visible Arabic
                  word beats an invisible element.
                </p>
                {schema.contentFields.map((spec) => (
                  <FieldEditor
                    key={spec.key}
                    spec={spec}
                    path={[spec.key]}
                    draft={draft}
                    onChange={setDraft}
                  />
                ))}
              </section>

              {schema.settingsFields.length > 0 && (
                <section className="space-y-5 rounded-2xl border border-admin-200 bg-white p-5">
                  <h2 className="text-sm font-semibold uppercase tracking-wider text-admin-400">
                    Settings
                  </h2>
                  {schema.settingsFields.map((spec) => (
                    <FieldEditor
                      key={spec.key}
                      spec={spec}
                      path={[spec.key]}
                      draft={draft}
                      onChange={setDraft}
                    />
                  ))}
                </section>
              )}
            </>
          ) : (
            <RawJsonFallback type={block.type} draft={draft} onChange={setDraft} />
          )}
        </div>

        {/* ── RIGHT: live preview ── */}
        <PreviewPane type={block.type} draft={draft} />
      </div>
    </div>
  );
}

// ─── Live preview ───────────────────────────────────────────────────────────

function PreviewPane({ type, draft }: { type: string; draft: BlockDraft }) {
  const [lang, setLang] = useState<'ar' | 'en'>('ar');
  const [viewport, setViewport] = useState<PreviewViewport>('1280');

  // Debounced payload — typing repaints the iframe at most every 350ms rather
  // than on every keystroke.
  const [settled, setSettled] = useState(draft);
  useEffect(() => {
    const timer = window.setTimeout(() => setSettled(draft), PREVIEW_DEBOUNCE_MS);
    return () => window.clearTimeout(timer);
  }, [draft]);

  const src = useMemo(
    () =>
      blockPreviewUrl(
        {
          type,
          content: settled.content,
          settings: settled.settings,
          // Pruned, so the preview renders exactly the payload a save would
          // send — an emptied English field falls through to Arabic here too.
          translations: pruneOverlays(settled.translations),
        },
        lang,
      ),
    [type, settled, lang],
  );

  const width = PREVIEW_VIEWPORTS.find((v) => v.value === viewport)?.width ?? 1280;
  // The frame is scaled down to fit the column, so 1280px of layout is visible
  // without a horizontal scrollbar. Measured, not guessed — the wrapper
  // reports its own width.
  const wrapperRef = useRef<HTMLDivElement>(null);
  const [available, setAvailable] = useState(720);
  useEffect(() => {
    const el = wrapperRef.current;
    if (!el) return;
    const observer = new ResizeObserver(([entry]) => {
      if (entry) setAvailable(entry.contentRect.width);
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  const scale = Math.min(1, available / width);

  return (
    <div className="xl:sticky xl:top-20 xl:self-start">
      <div className="overflow-hidden rounded-2xl border border-admin-200 bg-white">
        <div className="flex flex-wrap items-center justify-between gap-3 border-b border-admin-200 px-4 py-3">
          <div className="flex items-center gap-2">
            <span className="text-sm font-semibold text-admin-900">Live preview</span>
            <span className="text-xs text-admin-400">{Math.round(scale * 100)}%</span>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <Segmented
              size="sm"
              ariaLabel="Preview language"
              value={lang}
              options={[
                { value: 'ar', label: 'العربية' },
                { value: 'en', label: 'English' },
              ]}
              onChange={setLang}
            />
            <Segmented
              size="sm"
              ariaLabel="Preview viewport"
              value={viewport}
              options={PREVIEW_VIEWPORTS.map((v) => ({ value: v.value, label: v.label }))}
              onChange={setViewport}
            />
          </div>
        </div>

        <div ref={wrapperRef} className="overflow-hidden bg-admin-100 p-4">
          <div
            style={{ height: 640 * scale }}
            className="mx-auto overflow-hidden rounded-xl bg-white shadow-sm"
          >
            <iframe
              key={`${lang}-${viewport}`}
              src={src}
              title="Block preview"
              // Same-origin data would let the preview reach into the admin
              // session; it never needs to, so keep it sandboxed to scripts +
              // its own origin only.
              sandbox="allow-scripts allow-same-origin"
              style={{
                width,
                height: 640,
                transform: `scale(${scale})`,
                transformOrigin: 'top left',
              }}
              className="border-0"
            />
          </div>
        </div>
      </div>

      <p className="mt-2 text-xs text-admin-500">
        Rendered by the marketing site itself at <span className="font-mono">/preview/block</span> —
        what you see is the real block. It refreshes {PREVIEW_DEBOUNCE_MS}ms after you stop typing,
        before you save.
      </p>
    </div>
  );
}

// ─── Unknown type → raw JSON (graceful degradation) ─────────────────────────

function RawJsonFallback({
  type,
  draft,
  onChange,
}: {
  type: string;
  draft: BlockDraft;
  onChange: (next: BlockDraft) => void;
}) {
  return (
    <section className="space-y-4 rounded-2xl border border-admin-200 bg-white p-5">
      <Alert tone="warning">
        This admin build has no field editor for <span className="font-mono text-xs">{type}</span>.
        Editing the raw JSON instead — the API still validates it.
      </Alert>
      <JsonBox
        label="Content (Arabic base)"
        value={draft.content}
        onChange={(next) => onChange({ ...draft, content: next })}
      />
      <JsonBox
        label="Settings"
        value={draft.settings}
        onChange={(next) => onChange({ ...draft, settings: next })}
      />
      <JsonBox
        label="Translations (per-locale overlay)"
        value={draft.translations}
        onChange={(next) =>
          onChange({ ...draft, translations: next as Record<string, Record<string, unknown>> })
        }
      />
    </section>
  );
}

function JsonBox({
  label,
  value,
  onChange,
}: {
  label: string;
  value: Record<string, unknown>;
  onChange: (next: Record<string, unknown>) => void;
}) {
  const [text, setText] = useState(() => JSON.stringify(value, null, 2));
  const [invalid, setInvalid] = useState(false);

  return (
    <label className="block">
      <span className="text-sm font-semibold text-admin-900">{label}</span>
      <Textarea
        value={text}
        rows={Math.min(20, Math.max(6, text.split('\n').length))}
        onChange={(e) => {
          const next = e.target.value;
          setText(next);
          try {
            const parsed: unknown = JSON.parse(next);
            if (parsed !== null && typeof parsed === 'object' && !Array.isArray(parsed)) {
              setInvalid(false);
              onChange(parsed as Record<string, unknown>);
            } else {
              setInvalid(true);
            }
          } catch {
            setInvalid(true);
          }
        }}
        className="mt-2 font-mono text-xs leading-relaxed"
      />
      {invalid && <span className="mt-1 block text-xs text-heart">Not a valid JSON object.</span>}
    </label>
  );
}

// ─── Overlay hygiene ────────────────────────────────────────────────────────

/**
 * Drop empty overlay objects before sending. Editing then clearing every
 * English string would otherwise leave `{ en: {} }` on the row, which reads as
 * "this block has an English translation" everywhere it is counted.
 */
function pruneOverlays(
  translations: Record<string, Record<string, unknown>>,
): Record<string, Record<string, unknown>> {
  const out: Record<string, Record<string, unknown>> = {};
  for (const [locale, overlay] of Object.entries(translations)) {
    const cleaned = pruneOverlayValue(overlay);
    if (isPlainObject(cleaned) && Object.keys(cleaned).length > 0) {
      out[locale] = cleaned;
    }
  }
  return out;
}

function isPlainObject(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

/**
 * Normalise one overlay subtree for the wire.
 *
 * Two things have to be true of what we POST. First, an array must NOT carry
 * `undefined` holes: the editor pads the overlay array to match content's
 * length when reordering, and `JSON.stringify` renders a hole as `null`, which
 * the overlay schema's `.optional()` members reject. Holes become `{}`.
 * Second, an overlay key whose value is empty must be dropped entirely, or
 * `resolveBlockContent` sees a present-but-blank override where it should have
 * fallen through to the Arabic base.
 */
function pruneOverlayValue(value: unknown): unknown {
  if (Array.isArray(value)) {
    return value.map((item) => {
      const cleaned = pruneOverlayValue(item);
      return cleaned === undefined ? {} : cleaned;
    });
  }
  if (isPlainObject(value)) {
    const out: Record<string, unknown> = {};
    for (const [key, item] of Object.entries(value)) {
      const cleaned = pruneOverlayValue(item);
      if (cleaned !== undefined) out[key] = cleaned;
    }
    return out;
  }
  if (typeof value === 'string') return value.trim() === '' ? undefined : value;
  if (value === null) return undefined;
  return value;
}
